import { faBusAlt } from "@fortawesome/pro-solid-svg-icons"
import { Autocomplete, Box, Button, List, MenuItem, Select, TextField, useTheme } from "@mui/material"
import { useApiGet } from "auth/useApi2"
import SidebarInputLabel from "components/sidedrawer/SidebarInputLabel"
import { Formik, useFormikContext } from "formik"
import { apiEndpoints } from "generated/apiEndpoints"
import React, { useContext, useEffect, useRef, useState } from "react"
import Moment from "react-moment"
import { StyledListItem, StyledTextField } from "shared/styled/General"
import { VehicleAlarmChip } from "../VehicleAlarmChip"
import { StyledFormControl } from "./VehicleList.styled"
import * as Yup from "yup"
import SidebarHeader from "components/sidedrawer/SidebarHeader"
import NonFormikSidebar from "components/sidedrawer/NonFormikSidebar"
import SubmitButton from "components/Button"
import { StyledStickyButtonBox } from "components/sidedrawer/Sidebar.styled"
import { t } from "i18next"
import { AppContext } from "components/AppContextProvider"

export const status = (value) => {
  switch (value) {
    case 1:
      return <VehicleAlarmChip status="Pågående" label={t("global.ongoing", "Ongoing")} />
    case 2:
      return <VehicleAlarmChip status="Avslutad" label={t("global.completed", "Completed")} />
    default:
      return <VehicleAlarmChip status="Aktiv" label={t("global.active", "Active")} />
  }
}

export const alarmType = (value) => {
  switch (value) {
    case 1:
      return t("vehiclealarm.alcohol_lock", "Alcohol lock")
    default:
      return t("vehiclealarm.drivers_cards", "Drivers cards")
  }
}

const VehicleAlarmForm = ({
  formData,
  usersResult,
  selectedAlarmIds,
  updateVehicleResult,
  handleOnSubmit,
  t
}) => {
  const { values, setFieldValue, handleChange, handleBlur, errors, touched } = useFormikContext()
  const isMultipleAlarmsSelected = Array.isArray(selectedAlarmIds) && selectedAlarmIds.length > 1
  const theme = useTheme()

  return (
    <>
      <Box pt={12} pb={5}>
        {!isMultipleAlarmsSelected ? (
          <SidebarHeader
            icon={faBusAlt}
            backgroundColor={theme.palette.primary[300]}
            color={theme.palette.primary[800]}
            size="lg"
            title={formData.vehicle.regNumber}
            subtitle={formData.vehicle.internnumber}
            footer={status(formData.status)}
          />
        ) : (
          <SidebarHeader
            icon={faBusAlt}
            backgroundColor={theme.palette.primary[300]}
            color={theme.palette.primary[800]}
            size="lg"
            title={
              t("global.handles", "Handles") +
              " " +
              selectedAlarmIds.length +
              " " +
              t("vehclealarm.vehclealarm", "Vehclealarm")
            }
          />
        )}
      </Box>
      <List sx={{ display: "flex", flexDirection: "column", gap: "16px", height: "100%", p: 0 }}>
        {!isMultipleAlarmsSelected && (
          <>
            <StyledListItem>
              <SidebarInputLabel
                labelText={t("global.vehicle_id", "Vehicle id")}
                valueText={formData.vehicle.internnumber}
              />
            </StyledListItem>
            <StyledListItem>
              <SidebarInputLabel
                labelText={t("global.license_plate", "License plate")}
                valueText={formData.vehicle.regNumber}
              />
            </StyledListItem>
            <StyledListItem>
              <SidebarInputLabel
                labelText={t("vehiclealarm.alarm_type", "Alarm type")}
                valueText={alarmType(formData.alarmType, t)}
              />
            </StyledListItem>
            <StyledListItem>
              <SidebarInputLabel
                labelText={t("vehiclealarm.alarm_time", "Alarm time")}
                valueText={<Moment format="YYYY-MM-DD HH:mm">{formData.alarmDate}</Moment>}
              />
            </StyledListItem>
          </>
        )}
        <StyledListItem>
          <SidebarInputLabel labelText={t("deviation.action", "Action")} />
          <StyledTextField
            autoComplete="off"
            fullWidth
            id="measure"
            value={values.measure}
            onChange={handleChange}
            variant="outlined"
            size="small"
            InputProps={{
              disableUnderline: true
            }}
            onBlur={handleBlur}
            error={touched.measure && errors.measure ? true : false}
            helperText={touched.measure && errors.measure ? errors.measure : null}
          />
        </StyledListItem>
        <StyledListItem>
          <SidebarInputLabel labelText={t("global.responsible", "Responsible")} />
          <StyledFormControl>
            {usersResult.status === 2 && (
              <Autocomplete
                id="userid-autocomplete"
                options={usersResult?.data}
                getOptionLabel={(option) => (option.name ? option.name : "")}
                value={usersResult.data.find((i) => i.id === values.userId) || null}
                onChange={(e, value) => {
                  setFieldValue("userId", value?.id || null)
                  setFieldValue("user", value || null)
                }}
                onOpen={handleBlur}
                includeInputInList
                renderInput={(params) => (
                  <TextField
                    {...params}
                    name="userId"
                    variant="outlined"
                    error={touched.userId && errors.userId ? true : false}
                    helperText={touched.userId && errors.userId ? errors.userId : null}
                  />
                )}
              />
            )}
          </StyledFormControl>
        </StyledListItem>
        <StyledListItem>
          <SidebarInputLabel labelText={t("global.status", "Status")} />
          <StyledFormControl FormControl variant="outlined">
            <Select
              id="status"
              value={values.status}
              onChange={(e) => {
                setFieldValue("status", e.target.value)
              }}
              MenuProps={{
                disableScrollLock: true
              }}>
              <MenuItem value={0}>{t("global.active", "Active")}</MenuItem>
              <MenuItem value={1}>{t("global.ongoing", "Ongoing")}</MenuItem>
              <MenuItem value={2}>{t("global.completed", "Completed")}</MenuItem>
            </Select>
          </StyledFormControl>
        </StyledListItem>
      </List>
    </>
  )
}

const VehicleList = ({
  formData,
  onSubmit,
  updateVehicleResult,
  selectedAlarmIds,
  setSelectedAlarmIds,
  handleOnSubmit
}) => {
  const isMultipleAlarmsSelected = Array.isArray(selectedAlarmIds) && selectedAlarmIds.length > 1
  const initialValues = isMultipleAlarmsSelected
    ? { measure: "", userId: null, user: null, status: "" }
    : formData
  const [initialFormikValues, setInitialFormikValues] = useState(formData)
  const [usersResult, getUsers] = useApiGet()
  const appContext = useContext(AppContext)

  useEffect(() => {
    setInitialFormikValues(formData)
  }, [formData])

  useEffect(() => {
    if (formData != null)
      getUsers(
        apiEndpoints.vehiclealarm.getresponsibleusersforvehiclealarmeditresponsibledropdown.replace(
          "{vehicleAlarmId}",
          formData.id
        )
      )
  }, [getUsers, formData])

  const handleSubmit = async (values) => {
    onSubmit(values)
    setSelectedAlarmIds([])
  }

  const validationSchema = Yup.object().shape({
    measure: Yup.string().nullable().required(t("global.action_is_required", "Action is required")),
    userId: Yup.string().nullable().required(t("global.responsible_is_required", "Responsible is required")),
    status: Yup.string().nullable().required(t("global.status_is_required", "Status is required"))
  })

  const formRef = useRef()

  return initialFormikValues?.id ? (
    <NonFormikSidebar
      buttonChildren={
        <StyledStickyButtonBox p={{ xs: 3, sm: 4 }}>
          <SubmitButton
            result={updateVehicleResult}
            style={{ width: "100%" }}
            color="primary"
            onClick={() => handleSubmit(formRef?.current?.values)}
            variant="contained">
            {t("global.save_changes", "Save changes")}
          </SubmitButton>
          <Button
            variant="outlined"
            onClick={() => {
              appContext.toggleSideBar("VehicleAlarm", false)
            }}>
            {t("global.buttons.cancel", "Cancel")}
          </Button>
        </StyledStickyButtonBox>
      }
      sidebarId="VehicleAlarm">
      <Formik
        innerRef={formRef}
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize={true}>
        {({ handleSubmit }) => (
          <form onSubmit={handleSubmit} noValidate style={{ height: "100%" }}>
            <VehicleAlarmForm
              t={t}
              handleOnSubmit={handleSubmit}
              formData={formData}
              usersResult={usersResult}
              selectedAlarmIds={selectedAlarmIds}
              setSelectedAlarmIds={setSelectedAlarmIds}
              updateVehicleResult={updateVehicleResult}
            />
          </form>
        )}
      </Formik>
    </NonFormikSidebar>
  ) : null
}

export default VehicleList
